/**
 * @file    calculationPayload.ts
 * @scope   SHARED
 * @summary Builds the per-calculation query payload from a configured field: resolves the response
 *          path, normalization values, filters/conditions and the calc-type-specific shape.
 * @consumes SelectedFieldModel/FieldFilter, PROJECT_FREQUENCY_CALC_TYPE
 * @produces Field → query-collection-field mappers, filter → condition mappers, operator helpers
 * @sideEffects None — pure payload construction.
 * @notes Path rules mirror the backend contract: 'rating' for RatingQuestion, '' for
 *        SimpleBoolean/ProjectFrequency (no id/rating path), 'id' otherwise.
 */
import { SelectedFieldModel, FieldFilter } from 'models/Enterprise/FieldsManagerModel';
import { PROJECT_FREQUENCY_CALC_TYPE } from './projectFrequency';
import { usesNumericSettings } from '../views/partitionsManager/calculationsManager/numericSettingsUtils';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Shared context passed to every payload builder. */
interface FieldContext {
  /** The selected field entry from the FieldsManager form. */
  field: SelectedFieldModel;
  /** Spread copy of the raw CalculationOptionModel as a starting payload. */
  base: Record<string, unknown>;
  /** Resolved response path — 'rating' for RatingQuestion fields, '' for SimpleBoolean/ProjectFrequency, 'id' otherwise. */
  path: string;
  /** Response values eligible for normalization (excludes flagged responses like DK/NA/REF). */
  normalizeValues: (number | string | boolean)[];
}

/**
 * Determines the response path based on the field's question type.
 * @param field - The selected field to inspect
 * @returns 'rating' for RatingQuestion, '' for SimpleBoolean/numeric/ProjectFrequency, 'id' for all others
 */
const resolvePath = (field: SelectedFieldModel): string => {
  const typeName = field.field?.typeName;
  if (typeName === 'RatingQuestion') return 'rating';
  if (typeName === 'SimpleBoolean') return ''; // Boolean values (true/false) have no id/rating path
  if (usesNumericSettings(field)) return '';
  if (typeName === PROJECT_FREQUENCY_CALC_TYPE) return '';
  return 'id';
};

/**
 * Collects normalize values from the field's responses, excluding flagged entries (DK, NA, REF).
 * @param field - The selected field containing response data
 * @param path - The resolved response path ('rating', 'id', or '')
 * @returns Array of values (number/string/boolean) used for normalization
 */
const collectNormalizeValues = (
  field: SelectedFieldModel,
  path: string
): (number | string | boolean)[] => {
  if (!field.field?.responses?.length) return [];

  // For SimpleBoolean fields, return both boolean values
  if (field.field?.typeName === 'SimpleBoolean') {
    return [true, false];
  }

  // Exclude flagged responses (DK, NA, REF) from normalization
  return field.field.responses
    .filter((r: any) => r != null && r.flag == null)
    .map((r: any) => {
      if (path === 'rating') return Number(r.rating);
      if (path === '') return r.value; // For empty-path types
      return Number(r.id);
    });
};

/**
 * Builds a base metric descriptor used as a sub-field in calculation payloads.
 * Uses camelCase property names (new standard).
 * @param fieldNumber - The sequential field position
 * @param columnName - The API column name of the field
 * @param path - The resolved response path
 * @param suffix - Optional suffix appended to the displayName (e.g. 'Avg', 'Std')
 * @returns A metric descriptor object in camelCase format
 */
const buildBaseMetric = (
  field: SelectedFieldModel,
  path: string,
  suffix = ''
): Record<string, unknown> => ({
  displayName: `Metric${field.fieldNumber}${suffix}`,
  fieldName: field.field?.columnName,
  path: path,
  type: '2',
  attributeKind: field.field?.typeName === 'MultipleChoiceQuestion' ? 'Collection' : 'Scalar',
});

/**
 * Maps UI operator symbols to the FilterCondition.operator names used by the
 * nested metric/partition filter grammar (see FE Guide - MetricFilters.md).
 */
const OPERATOR_SYMBOL_TO_API_OPERATOR: Record<string, string> = {
  '=': 'equals',
  '!=': 'notEquals',
  IN: 'in',
  'NOT IN': 'notIn',
  '>': 'greaterThan',
  '>=': 'greaterThanOrEqual',
  '<': 'lessThan',
  '<=': 'lessThanOrEqual',
  // Not part of the documented grammar; kept for fields whose OperatorOptions still offer these.
  CONTAINS: 'contains',
  'NOT CONTAINS': 'notContains',
};

export const toApiOperator = (op: string): string => OPERATOR_SYMBOL_TO_API_OPERATOR[op] ?? op;

/** Legacy PascalCase enum names produced before the nested filter grammar existed. */
const LEGACY_API_OPERATOR_TO_SYMBOL: Record<string, string> = {
  Equals: '=',
  NotEquals: '!=',
  In: 'IN',
  NotIn: 'NOT IN',
  GreaterThan: '>',
  GreaterThanOrEquals: '>=',
  LessThan: '<',
  LessThanOrEquals: '<=',
  Contains: 'CONTAINS',
  NotContains: 'NOT CONTAINS',
};

/** Reverse lookup: API operator name → UI operator symbol. Recognizes both the current
 * camelCase names and the legacy PascalCase names so older saved widgets still round-trip. */
const API_OPERATOR_TO_SYMBOL: Record<string, string> = {
  ...LEGACY_API_OPERATOR_TO_SYMBOL,
  // Names emitted by earlier builds (the backend rejects them); still readable from saved widgets.
  greaterThanOrEqual: '>=',
  greaterThanOrEqualTo: '>=',
  lessThanOrEqual: '<=',
  lessThanOrEqualTo: '<=',
  greaterThanOrEquals: '>=',
  lessThanOrEquals: '<=',
  GreaterThanOrEqualTo: '>=',
  LessThanOrEqualTo: '<=',
  ...Object.fromEntries(
    Object.entries(OPERATOR_SYMBOL_TO_API_OPERATOR).map(([symbol, apiOperator]) => [
      apiOperator,
      symbol,
    ])
  ),
};

/**
 * Maps backend FilterCondition operator names back to the UI operator symbol.
 * If the value is already a symbol (or unknown) it passes through unchanged.
 */
export const fromApiOperator = (op: string): string => API_OPERATOR_TO_SYMBOL[op] ?? op;

/**
 * Maps a FieldFilter to a FilterCondition per the nested metric/partition filter grammar:
 * camelCase attributeName/operator/values/attributeKind, values typed as number | string | boolean.
 * Shared by metricFilter (field level) and partitionFilter (partition level) so both
 * payloads stay in the exact same shape.
 * @param filter - A single FieldFilter from a filters array
 * @returns A condition object matching the API FilterCondition shape
 */
export const mapFilterToCondition = (filter: FieldFilter): Record<string, unknown> => {
  // Id/rating values are option codes with a choice label; numbers on raw columns are the data itself.
  const isChoicePath = /\.(id|rating)$/.test(filter.attributePath ?? '');
  return {
    attributeName: filter.attributePath || filter.filterName,
    attributeFriendlyName: filter.filterName || filter.attributePath,
    operator: toApiOperator(filter.operator),
    values: filter.selectedValues.map((v): number | string | boolean => v.value),
    friendlyValues: filter.selectedValues.map((v) =>
      typeof v.value === 'number' && !isChoicePath ? String(v.value) : v.label ?? String(v.value)
    ),
    attributeKind: filter.attributeKind === 'Collection' ? 'collection' : 'scalar',
  };
};

/**
 * Builds a nested FilterGroup from a flat, pairwise-joined filter list.
 * Filters are split into AND-runs at each OR boundary, matching standard operator
 * precedence (AND binds tighter than OR): "A AND B OR C AND D" → (A AND B) OR (C AND D).
 * A single run needs no nesting; multiple runs are combined with a top-level "or" —
 * single-filter runs sit directly in its `conditions`, multi-filter runs become nested
 * `groups` (see the "Nested AND/OR" example in FE Guide - MetricFilters.md).
 * @param filters - The flat filter list, each carrying the operator joining it to the previous filter
 * @returns A FilterGroup object, or undefined when there are no filters
 */
export const buildFilterGroup = (filters: FieldFilter[]): Record<string, unknown> | undefined => {
  if (!filters || filters.length === 0) return undefined;

  const runs: FieldFilter[][] = [[filters[0]]];
  for (let i = 1; i < filters.length; i++) {
    if (filters[i].logicalOperator === 'OR') runs.push([filters[i]]);
    else runs[runs.length - 1].push(filters[i]);
  }

  if (runs.length === 1) {
    return { logic: 'and', conditions: runs[0].map(mapFilterToCondition) };
  }

  const conditions: Record<string, unknown>[] = [];
  const groups: Record<string, unknown>[] = [];
  runs.forEach((run) => {
    if (run.length === 1) conditions.push(mapFilterToCondition(run[0]));
    else groups.push({ logic: 'and', conditions: run.map(mapFilterToCondition) });
  });

  return { logic: 'or', conditions, ...(groups.length ? { groups } : {}) };
};

/**
 * Builds a metricFilter object from a field's filters array.
 * @param field - The selected field that may contain advanced filters
 * @returns A FilterGroup object or undefined if no filters are configured
 */
const buildMetricFilter = (field: SelectedFieldModel): Record<string, unknown> | undefined =>
  buildFilterGroup(field.filters ?? []);

/**
 * Builds the common payload properties shared across all calculation types.
 * Uses camelCase property names (new standard).
 * @param ctx - The field context with base payload, path and normalize values
 * @param type - The calculation type identifier ('1'–'4')
 * @returns A payload object with base, decimalPrecision, displayName, and other shared fields
 */
const sharedPayload = (ctx: FieldContext, type: string): Record<string, unknown> => {
  const metricFilter = buildMetricFilter(ctx.field);
  return {
    ...ctx.base,
    base: buildBaseMetric(ctx.field, ctx.path),
    decimalPrecision: Number(ctx.field.precision),
    displayName: ctx.field.displayName,
    metadata: {
      rowType: ctx.field.isL1Category ? 'L1' : 'L2',
    },
    isL1Category: ctx.field.isL1Category ?? false,
    normalize: ctx.field.normalize,
    normalizeValues: ctx.normalizeValues,
    statTesting: ctx.field.statTesting,
    type: type,
    ...(metricFilter ? { metricFilter } : {}),
  };
};

// ---------------------------------------------------------------------------
// Strategy: one builder per calculation type
// ---------------------------------------------------------------------------

type PayloadBuilder = (ctx: FieldContext) => Record<string, unknown>;

/**
 * Type 1 — Mean: includes avg and stdDev sub-metrics alongside the shared payload.
 */
const buildMeanPayload: PayloadBuilder = (ctx) => ({
  ...sharedPayload(ctx, '1'),
  avg: {
    displayName: `Metric${ctx.field.fieldNumber}Avg`,
    fieldName: ctx.field.field?.columnName,
    path: ctx.path,
    type: '3',
  },
  stdDev: {
    displayName: `Metric${ctx.field.fieldNumber}Std`,
    fieldName: ctx.field.field?.columnName,
    path: ctx.path,
    type: '4',
  },
});

/**
 * Type 6 — Median: includes a median sub-metric but never normalization values.
 */
const buildMedianPayload: PayloadBuilder = (ctx) => ({
  ...sharedPayload(ctx, '6'),
  median: {
    displayName: `Metric${ctx.field.fieldNumber}Med`,
    fieldName: ctx.field.field?.columnName,
    path: ctx.path,
    type: '6',
  },
});

/**
 * ValueCount for numeric fields: raw numeric values plus an optional ValueOperator ('equals' is omitted).
 * Returns undefined when no values are configured.
 */
const buildNumericValueCount = (ctx: FieldContext): Record<string, unknown> | undefined => {
  const settings = ctx.field.numericSettings;
  if (!settings?.values.length) return undefined;
  return {
    displayName: `Metric${ctx.field.fieldNumber}Val`,
    fieldName: ctx.field.field?.columnName,
    path: ctx.path,
    type: ctx.field.calculationType?.valueCount?.type ?? '1',
    values: [...settings.values],
    ...(settings.valueOperator !== 'equals' ? { valueOperator: settings.valueOperator } : {}),
    attributeKind: 'Scalar',
  };
};

/**
 * Type 2 — Percentage: adds a valueCount sub-metric.
 * Uses the user's selected values from percentageSettings when available,
 * falling back to the top-2 response values sorted descending.
 * Supports SimpleBoolean (true/false), flags (DK/NA/REF as strings), and numeric values.
 */
const buildPercentagePayload: PayloadBuilder = (ctx) => {
  if (usesNumericSettings(ctx.field)) {
    const valueCount = buildNumericValueCount(ctx);
    return { ...sharedPayload(ctx, '2'), ...(valueCount ? { ValueCount: valueCount } : {}) };
  }

  const isSimpleBoolean = ctx.field.field?.typeName === 'SimpleBoolean';

  // For SimpleBoolean, fallback is [true] (Yes)
  const fallbackValues = isSimpleBoolean
    ? [true]
    : ctx.path === 'rating'
    ? ctx.field.field?.responses
        ?.filter((r: any) => r.flag == null)
        .map((r: any) => Number(r.rating))
        .sort((a: number, b: number) => b - a)
        .slice(0, 2) ?? []
    : ctx.field.field?.responses
        ?.filter((r: any) => r.flag == null)
        .map((r: any) => Number(r.id))
        .sort((a: number, b: number) => b - a)
        .slice(0, 2) ?? [];

  // Normalize a single value: numeric → integer, flags (DK/NA/REF) → string, booleans → as-is
  const normalizeValue = (v: number | string | boolean): number | string | boolean => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const num = Number(v);
      return isNaN(num) ? v : Math.trunc(num);
    }
    return Math.trunc(v);
  };

  // Extract values - numeric as Int, flags as string, booleans as-is
  const selectedValues =
    ctx.field.percentageSettings?.selectedValues
      .map((v) => normalizeValue(v.value))
      .sort((a, b) => {
        // Sort: booleans > numbers > strings, then by value
        if (typeof a === 'boolean' && typeof b !== 'boolean') return -1;
        if (typeof a !== 'boolean' && typeof b === 'boolean') return 1;
        if (typeof a === 'number' && typeof b === 'number') return b - a;
        return 0;
      }) ?? fallbackValues;

  const selectedValuePaths =
    ctx.field.percentageSettings?.selectedValues?.map((v) => v.path?.toLocaleLowerCase()) ?? [];

  let mixedValueMatchMode = 'All';
  const flag =
    selectedValuePaths.includes('dk') ||
    selectedValuePaths.includes('na') ||
    selectedValuePaths.includes('ref');
  const notFlag = selectedValuePaths.includes('id') || selectedValuePaths.includes('rating');
  if (flag && notFlag) mixedValueMatchMode = 'Any';

  return {
    ...sharedPayload(ctx, '2'),
    valueCount: {
      displayName: `Metric${ctx.field.fieldNumber}Val`,
      fieldName: ctx.field.field?.columnName,
      path: flag && !notFlag ? 'flag' : ctx.path,
      type: ctx.field.calculationType?.valueCount?.type ?? '1',
      values: selectedValues,
      mixedValueMatchMode: mixedValueMatchMode,
      attributeKind:
        ctx.field.field?.typeName === 'MultipleChoiceQuestion' ? 'Collection' : 'Scalar',
    },
  };
};

/**
 * Type 3 — NPS: splits responses into promoters, neutrals and detractors
 * based on configurable thresholds (defaults: detractors ≤ 6, promoters ≥ 9).
 * NPS always uses a fixed 0-10 scale, so we generate the value arrays directly
 * from the thresholds rather than iterating over Responses (which may be incomplete
 * when coming from edit mode).
 */
const buildNpsPayload: PayloadBuilder = (ctx) => {
  const detractorsMax = ctx.field.npsSettings?.detractorsMax ?? 6;
  const promotersMin = ctx.field.npsSettings?.promotersMin ?? 9;

  // NPS uses a fixed 0-10 scale; generate arrays directly from thresholds
  const detractorValues: number[] = [];
  const neutralValues: number[] = [];
  const promoterValues: number[] = [];

  for (let i = 0; i <= 10; i++) {
    if (i <= detractorsMax) detractorValues.push(i);
    else if (i >= promotersMin) promoterValues.push(i);
    else neutralValues.push(i);
  }

  // Sort descending to match expected payload format
  detractorValues.sort((a, b) => b - a);
  neutralValues.sort((a, b) => b - a);
  promoterValues.sort((a, b) => b - a);

  const metricSuffix = (s: string) => ({
    displayName: `Metric${ctx.field.fieldNumber}${s}`,
    fieldName: ctx.field.field?.columnName,
    path: ctx.path,
    type: '1',
  });

  return {
    ...sharedPayload(ctx, '3'),
    detractors: { ...metricSuffix('Det'), values: detractorValues },
    neutrals: { ...metricSuffix('Neu'), values: neutralValues },
    promoters: { ...metricSuffix('Pro'), values: promoterValues },
  };
};

/**
 * Type 4 — Total Count: replaces the base metric with a totalMetric sub-field.
 */
const buildTotalCountPayload: PayloadBuilder = (ctx) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { base: _base, ...rest } = sharedPayload(ctx, '4');

  // Normalize a single value: numeric → integer, flags (DK/NA/REF) → string, booleans → as-is
  const normalizeValue = (v: number | string | boolean): number | string | boolean => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const num = Number(v);
      return isNaN(num) ? v : Math.trunc(num);
    }
    return Math.trunc(v);
  };

  // Extract values - numeric as Int, flags as string, booleans as-is
  const selectedValues =
    ctx.field.percentageSettings?.selectedValues
      .map((v) => normalizeValue(v.value))
      .sort((a, b) => {
        // Sort: booleans > numbers > strings, then by value
        if (typeof a === 'boolean' && typeof b !== 'boolean') return -1;
        if (typeof a !== 'boolean' && typeof b === 'boolean') return 1;
        if (typeof a === 'number' && typeof b === 'number') return b - a;
        return 0;
      }) ?? [];
  const numericValueCount = buildNumericValueCount(ctx);

  return {
    ...rest,
    totalMetric: {
      displayName: `TOTAL${ctx.field.fieldNumber}`,
      fieldName: ctx.field.field?.columnName,
      path: ctx.path,
      type: '5',
    },
    ...(usesNumericSettings(ctx.field)
      ? numericValueCount
        ? { ValueCount: numericValueCount }
        : {}
      : selectedValues.length > 0
      ? {
          valueCount: {
            displayName: `Metric${ctx.field.fieldNumber}Val`,
            fieldName: ctx.field.field?.columnName,
            path: ctx.path,
            type: ctx.field.calculationType?.valueCount?.type ?? '1',
            values: selectedValues,
            mixedValueMatchMode: 'All',
            attributeKind:
              ctx.field.field?.typeName === 'MultipleChoiceQuestion' ? 'Collection' : 'Scalar',
          },
        }
      : {}),
  };
};

const buildProjectFrequencyPayload: PayloadBuilder = (ctx) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { base: _base, ...rest } = sharedPayload(ctx, '4');
  return {
    ...rest,
    totalMetric: {
      displayName: `TOTAL${ctx.field.fieldNumber}`,
      fieldName: ctx.field.field?.columnName,
      path: '',
      type: '5',
    },
    decimalPrecision: 0,
    displayName: ctx.field.displayName,
    metadata: { rowType: 'L1' },
  };
};

/**
 * Strategy map — add new calculation types here without touching existing builders.
 */
const PAYLOAD_BUILDERS: Record<string, PayloadBuilder> = {
  '1': buildMeanPayload,
  '2': buildPercentagePayload,
  '3': buildNpsPayload,
  '4': buildTotalCountPayload,
  '5': buildProjectFrequencyPayload,
  '6': buildMedianPayload,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Transforms a SelectedFieldModel into a calculation payload using the Strategy pattern.
 * Each calculation type is handled by a dedicated builder registered in PAYLOAD_BUILDERS.
 * Outputs camelCase property names, omitting normalizeValues for numeric fields and Median.
 */
export const buildCalculationTypePayload = (
  selectedField: SelectedFieldModel
): Record<string, unknown> | null => {
  const { calculationType } = selectedField;
  if (!calculationType) return null;

  const path = resolvePath(selectedField);
  const normalizeValues = collectNormalizeValues(selectedField, path);
  const base: Record<string, unknown> = Object.fromEntries(
    Object.entries(calculationType).map(([key, value]) => [
      key.charAt(0).toLowerCase() + key.slice(1),
      value,
    ])
  );
  const ctx: FieldContext = { field: selectedField, base, path, normalizeValues };

  const builder = PAYLOAD_BUILDERS[String(calculationType.type)];
  const payload = builder ? builder(ctx) : base;
  if (usesNumericSettings(selectedField) || String(calculationType.type) === '6') {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { normalizeValues: _normalizeValues, ...rest } = payload;
    return rest;
  }
  return payload;
};

/**
 * Maps a SelectedFieldModel to a queryCollections field for the widget config payload.
 * Outputs camelCase property names (new standard). */
export const mapSelectedFieldToQueryCollectionField = (
  field: SelectedFieldModel
): Record<string, unknown> | null => buildCalculationTypePayload(field);
